/**
 * Reset all player progress across the entire platform.
 * Clears: completed chronicles, memory flags, unlocked chronicles, game saves,
 * settings, AND the first-visit tutorial flag (so the "How to Play" carousel
 * fires again on the next StartScreen entry, matching a fresh-install
 * experience).
 */

/** Global guard – when true, all save operations should be suppressed. */
let _resetting = false;
export function isResetting(): boolean {
  return _resetting;
}

const ALL_STORAGE_KEYS = [
  "chronicles-completed",                       // useAchievements
  "achievements-seen",                          // useAchievements (badge state)
  "chronicles_memory_flags",                    // chronicleMemoryStore
  "chronicles-unlocked",                        // chronicleUnlockStore
  "chronicles_settings",                        // settingsStore
  // Tutorial first-visit flag — without this in the reset list, "Clear
  // all progress" wouldn't bring back the How-to-Play carousel, which
  // is part of the fresh-install experience the player expects to get
  // back. Keep this in sync with TUTORIAL_DONE_KEY in HowToPlayTutorial.
  "coe-tutorial-completed",                     // HowToPlayTutorial
  "coe-disclosures-ack-v1",                     // disclosures first-run ack, old version
  "coe-disclosures-ack-v2",                     // disclosures first-run ack (lib/disclosures)
  "chronicles_europe_save",                     // GameEngine (Eighty Years' War)
  "chronicles_europe_save_voc",                 // VocGameEngine (Dutch Golden Age)
  "chronicles_europe_save_burgundian",          // BurgundianGameEngine
  "chronicles_europe_save_belgian_revolution",  // BelgianRevolutionGameEngine
  "chronicles_europe_save_iberian_union",       // IberianUnionGameEngine
  "chronicles_europe_save_fall_of_granada",     // FallOfGranadaGameEngine
  "chronicles_europe_save_reconquista",         // ReconquistaGameEngine
  "chronicles_europe_save_peninsular_war",      // PeninsularWarGameEngine
  "chronicles_europe_save_first_voyages_west",  // FirstVoyagesWestGameEngine
  "chronicles_europe_save_spanish_civil_war",   // SpanishCivilWarGameEngine
  "chronicles_europe_save_hundred_years_war",   // HundredYearsWarGameEngine
  "chronicles_europe_save_french_revolution",   // FrenchRevolutionGameEngine
  "chronicles_europe_save_albigensian_crusade", // AlbigensianCrusadeGameEngine
  "chronicles_europe_save_viking_dublin",       // VikingDublinGameEngine
  "chronicles_europe_save_norman_conquest",     // NormanConquestGameEngine
  "chronicles_europe_save_the_anarchy",         // TheAnarchyGameEngine
  "chronicles_europe_save_wars_of_roses",       // WarsOfTheRosesGameEngine
  "chronicles_europe_save_english_civil_wars",  // EnglishCivilWarsGameEngine
  "chronicles_europe_save_irish_independence",  // IrishIndependenceGameEngine
];

export function resetAllProgress(): void {
  // Set guard so autosave handlers don't re-persist during reload
  _resetting = true;

  // Clear all known keys
  for (const key of ALL_STORAGE_KEYS) {
    localStorage.removeItem(key);
  }
  // Catch-all: remove any chronicles-prefixed keys we may have missed
  const toRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith("chronicles") || key.startsWith("chronicles_") || key.startsWith("chronicles-"))) {
      toRemove.push(key);
    }
  }
  for (const key of toRemove) {
    localStorage.removeItem(key);
  }

  // Clear session storage (map intro state, etc.)
  sessionStorage.clear();

  // Explicitly write fresh settings with music ENABLED (musicMuted:
  // false). Previously we relied on the absence of the settings key
  // for `load()` in settingsStore.tsx to fall through to defaults —
  // which currently happen to default to music-on, but that is
  // implicit and brittle (a future change to the defaults table
  // could silently leave the player muted after a reset). Writing
  // the desired post-reset state explicitly here makes the
  // contract clear: "Reset Progress = fresh-install experience,
  // music ON by default". Keep this in sync with the DEFAULTS
  // shape in settingsStore.tsx; if you add a new setting field
  // there, the load() spread still merges them in on top, so this
  // partial-payload approach stays forward-compatible.
  try {
    localStorage.setItem(
      "chronicles_settings",
      JSON.stringify({ musicMuted: false, masterVolume: 50 }),
    );
  } catch { /* private-mode / quota — fall through to load() defaults */ }
}

export function hasAnyProgress(): boolean {
  if (ALL_STORAGE_KEYS.some((key) => localStorage.getItem(key) !== null)) return true;
  // Also check for any chronicles-prefixed keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith("chronicles") || key.startsWith("chronicles_") || key.startsWith("chronicles-"))) {
      return true;
    }
  }
  return false;
}
